import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { LoaderCircle, Input, Table } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList, useDebounce } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { useSearchParamsByKey, useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import type { TradeInOrder } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

import { OrderListItem } from './OrderListItem'
import { StatusSelect } from './StatusSelect'
import { StatusTabs } from './StatusTabs'

import { ExportToExcel } from '../../../components/ExportToExcel'
import adminQueries from '../../../queries'

export const Orders: FC<{ customerId: number }> = ({ customerId }) => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const { data, isLoading } = useQuery(adminQueries.adminTradeIdOrdersList(customerId), () =>
    adminResources.getTradeInOrders(customerId)
  )
  const { locale } = useAppContext()

  const [querySearch, setSearchQuery] = useSearchParamsByKey('search')
  const [queryStatus] = useSearchParamsByKey('status', 'PRICE_REQUEST')
  const [{ querySort, querySortBy }, { handleSort }] = useSortParams<keyof TradeInOrder>()

  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const debouncedAssetSearchTerm = useDebounce(searchFilter, 500)

  const stringMatcher = (item: TradeInOrder) => [
    item.productName,
    item.imeiSerialNumber,
    translate(`trade_in_order_status/${item.orderStatus}`),
    item.estimatedPrice,
    item.finalGrade,
    item.finalPrice,
    item.grade,
    item.assetDate,
  ]

  useEffect(() => {
    setSearchFilter(querySearch || '')
  }, [querySearch])

  useEffect(() => {
    setSearchQuery(debouncedAssetSearchTerm)
  }, [debouncedAssetSearchTerm, setSearchQuery])

  const goToDashboard = () => navigate(APP_ROUTES.HOME)

  const itemsFiltered =
    data?.orders?.filter(item => {
      const isStatusFilterMet = queryStatus
        ? queryStatus === 'ALL'
          ? true
          : item.orderStatus === queryStatus
        : true
      return isStatusFilterMet && getQueryFilter(searchFilter, stringMatcher)(item)
    }) || []
  const sortedList = getSortedList({ list: itemsFiltered, querySort, querySortBy, locale })
  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList || [], 30)

  const showActionsCol =
    queryStatus === 'PRICE_ESTIMATION' || queryStatus === 'ACCEPTANCE' || queryStatus === 'ALL'

  if (isLoading)
    return (
      <Container onSwipedLeft={goToDashboard}>
        <Container.Header>
          <Translation id="Trade-in orders" />
        </Container.Header>
        <LoaderCircle />
      </Container>
    )

  return (
    <Container onSwipedLeft={goToDashboard}>
      <Container.Header>
        <Translation id="Trade-in orders" />
      </Container.Header>
      {data && <StatusTabs orderCounter={data.orderCounter} />}
      {data && <StatusSelect orderCounter={data.orderCounter} />}
      <div className="items-center justify-between md:flex">
        <div className="table-actions">
          <div className="search">
            <Input
              id="admin-trade-in-orders-search"
              type="text"
              value={searchFilter}
              placeholder={translate('Search')}
              onChange={e => setSearchFilter(e.target.value)}
            />
          </div>
        </div>
      </div>
      <ExportToExcel
        data={itemsFiltered}
        filename={translate('Trade-in orders')}
        options={{
          columnFormats: {
            purchaseDate: 'date',
            finalPrice: 'amount',
            estimatedPrice: 'amount',
          },
        }}
      />
      {itemsFiltered.length > 0 ? (
        <div>
          <Table rowHeight="tall">
            <thead>
              <tr>
                <Th id="productName" onClick={handleSort}>
                  <Translation id="Product name" />
                </Th>

                <Th id="assetDate" className="hidden md:table-cell" onClick={handleSort}>
                  <Translation id="Purchase date" />
                </Th>

                <Th id="grade" className="hidden md:table-cell" onClick={handleSort}>
                  <Translation id="Grade" />
                </Th>

                <Th id="estimatedPrice" className="hidden md:table-cell" onClick={handleSort}>
                  <Translation id="Estimated price" />
                </Th>

                <Th id="finalGrade" className="hidden md:table-cell" onClick={handleSort}>
                  <Translation id="Final grade" />
                </Th>

                <Th id="finalPrice" className="hidden md:table-cell" onClick={handleSort}>
                  <Translation id="Final price" />
                </Th>

                <Th id="orderStatus" onClick={handleSort}>
                  <Translation id="Status" />
                </Th>

                {showActionsCol && (
                  <Th>
                    <Translation id="Actions" />
                  </Th>
                )}
              </tr>
            </thead>

            {isLoading ? (
              <tbody>
                <tr>
                  <td>
                    <LoaderCircle />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {visibleList.map(item => (
                  <OrderListItem
                    key={item.orderId}
                    searchFilter={searchFilter}
                    showActionsCol={showActionsCol}
                    orderCustomerId={customerId}
                    {...item}
                  />
                ))}
              </tbody>
            )}
          </Table>
          <ListLoaderComponent />
        </div>
      ) : (
        <NoResults />
      )}
    </Container>
  )
}
